import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Plus, Send, Trash2, Search } from "lucide-react";
import { fetchWithAuth } from "@/lib/auth-client";
import type {
  RFQListItemDto,
  RFQDto,
  RFQLineItemDto,
  VendorDto,
  CreatePurchaseOrderRequest,
  UpdatePurchaseOrderRequest,
  PurchaseOrderDetailDto,
} from "@procureflow/shared-types";
import {
  rfqLineRemaining,
  validatePOBuildRows,
  type POBuildRow as POBuildRowModel,
} from "@/lib/po-build";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// US-064 (m2m-document-linkage) — Create-PO-from-multiple-RFQs builder.
//
// A standalone form reached from a "Create PO" button on the PO list. Each row
// has TWO cascading lookups: (1) pick a consumable source RFQ (Distributed /
// PartiallyConsumed with remaining orderable lines), (2) pick a line of that RFQ
// (only lines with remaining > 0). Each row also carries a negotiated unit_price
// (cents) — the PO records the price the RFQ line does not. A single vendor is
// assigned at PO creation. The form prevents over-consuming (qty <= remaining),
// then POSTs /api/purchase-orders and navigates to the new PO detail.
//
// The award→PO path (quotes award cascade) remains the single-source special case.

// US-077 — the same builder doubles as the draft-edit form when reached with
// ?editId=<poId> (from the Edit affordance on a Draft PO). In edit mode it pre-loads the
// PO's current lines/vendor/terms and PUTs the changes (the source RFQ re-sync happens
// server-side); otherwise it POSTs a new PO.
export const Route = createFileRoute("/purchase-orders_/build")({
  validateSearch: (search: Record<string, unknown>): { editId?: string } => ({
    editId: typeof search.editId === "string" ? search.editId : undefined,
  }),
  head: () => ({
    meta: [{ title: "Build PO — ProcureFlow" }],
  }),
  component: BuildPOPage,
});

interface RowState {
  key: string;
  sourceRfqId: string;
  sourceRfqNumber: string;
  sourceRfqLineItemId: string;
  lineDescription: string;
  lineUom: string;
  lineRemaining: number;
  quantity: number;
  // unit price in major-unit dollars for the input; converted to cents on submit.
  unitPriceDollars: string;
}

function newRow(): RowState {
  return {
    key: crypto.randomUUID(),
    sourceRfqId: "",
    sourceRfqNumber: "",
    sourceRfqLineItemId: "",
    lineDescription: "",
    lineUom: "",
    lineRemaining: 0,
    quantity: 1,
    unitPriceDollars: "",
  };
}

/** Parse a dollar string into integer cents; NaN-safe (returns 0 on blank). */
function dollarsToCents(value: string): number {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

function BuildPOPage() {
  const navigate = useNavigate();
  const { editId } = Route.useSearch();
  const isEdit = Boolean(editId);

  const [rfqs, setRfqs] = useState<RFQListItemDto[]>([]);
  const [vendors, setVendors] = useState<VendorDto[]>([]);
  const [vendorId, setVendorId] = useState<string>("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [deliveryTerms, setDeliveryTerms] = useState("");
  const [rows, setRows] = useState<RowState[]>([newRow()]);
  // Cache of RFQ detail (lines) by RFQ id, lazily fetched when an RFQ is picked.
  const [rfqDetails, setRfqDetails] = useState<Record<string, RFQDto>>({});
  const [loading, setLoading] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // fetchWithAuth awaits mswReady internally so the MSW mount-time fetch
        // pattern is satisfied without an explicit await here. Only consumable
        // RFQs (Distributed / PartiallyConsumed) can be ordered against.
        const [rfqRes, vRes] = await Promise.all([
          fetchWithAuth("/api/rfqs?status=Distributed&limit=100"),
          fetchWithAuth("/api/vendors?status=Active"),
        ]);
        const distributed = rfqRes.ok
          ? ((await rfqRes.json()) as { data: RFQListItemDto[] }).data
          : [];
        // Fetch PartiallyConsumed RFQs too (a second status query keeps the MSW
        // single-status filter simple).
        const partialRes = await fetchWithAuth("/api/rfqs?status=PartiallyConsumed&limit=100");
        const partial = partialRes.ok
          ? ((await partialRes.json()) as { data: RFQListItemDto[] }).data
          : [];
        const vJson = vRes.ok ? ((await vRes.json()) as { data: VendorDto[] }) : { data: [] };
        if (cancelled) return;
        setRfqs([...(distributed ?? []), ...(partial ?? [])]);
        setVendors((vJson.data ?? []).filter((v) => v.status === "Active"));

        // US-077 edit mode — pre-load the existing PO's vendor/terms/lines. The source
        // RFQ line's "remaining" is bumped by this PO's existing consumption so the user
        // can keep or raise their own quantities (server re-checks effective remaining).
        if (editId) {
          const poRes = await fetchWithAuth(`/api/purchase-orders/${editId}`);
          if (poRes.ok) {
            const { data: po } = (await poRes.json()) as { data: PurchaseOrderDetailDto };
            if (cancelled) return;
            setVendorId(po.vendor_id);
            setPaymentTerms(po.payment_terms ?? "");
            setDeliveryTerms(po.delivery_terms ?? "");
            const sourceRfqIds = [
              ...new Set(
                po.line_items.map((li) => li.source_rfq_id).filter((x): x is string => !!x),
              ),
            ];
            const detailEntries = await Promise.all(
              sourceRfqIds.map(async (rid) => {
                const r = await fetchWithAuth(`/api/rfqs/${rid}`);
                return r.ok ? ([rid, ((await r.json()) as { data: RFQDto }).data] as const) : null;
              }),
            );
            const detailMap: Record<string, RFQDto> = {};
            for (const e of detailEntries) if (e) detailMap[e[0]] = e[1];
            if (cancelled) return;
            setRfqDetails(detailMap);
            const editRows: RowState[] = po.line_items.map((li) => {
              const rfqDetail = li.source_rfq_id ? detailMap[li.source_rfq_id] : undefined;
              const rfqLine = rfqDetail?.line_items.find(
                (r) => r.id === li.source_rfq_line_item_id,
              );
              const currentRemaining = rfqLine ? rfqLineRemaining(rfqLine) : 0;
              return {
                key: crypto.randomUUID(),
                sourceRfqId: li.source_rfq_id ?? "",
                sourceRfqNumber: li.source_rfq_number ?? "",
                sourceRfqLineItemId: li.source_rfq_line_item_id ?? "",
                lineDescription: li.description,
                lineUom: li.unit_of_measure,
                lineRemaining: currentRemaining + li.quantity,
                quantity: li.quantity,
                unitPriceDollars: (li.unit_price / 100).toString(),
              };
            });
            if (editRows.length > 0) setRows(editRows);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [editId]);

  // Lines of an RFQ with remaining > 0, from the cached detail.
  function availableLines(rfqId: string): RFQLineItemDto[] {
    const detail = rfqDetails[rfqId];
    if (!detail) return [];
    return detail.line_items.filter((li) => rfqLineRemaining(li) > 0);
  }

  async function pickRfq(rowKey: string, rfq: RFQListItemDto) {
    if (!rfqDetails[rfq.id]) {
      const res = await fetchWithAuth(`/api/rfqs/${rfq.id}`);
      if (res.ok) {
        const { data } = (await res.json()) as { data: RFQDto };
        setRfqDetails((prev) => ({ ...prev, [rfq.id]: data }));
      }
    }
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              sourceRfqId: rfq.id,
              sourceRfqNumber: rfq.rfq_number,
              // Reset the line selection when the RFQ changes.
              sourceRfqLineItemId: "",
              lineDescription: "",
              lineUom: "",
              lineRemaining: 0,
            }
          : r,
      ),
    );
  }

  function pickLine(rowKey: string, line: RFQLineItemDto) {
    const remaining = rfqLineRemaining(line);
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              sourceRfqLineItemId: line.id,
              lineDescription: line.description,
              lineUom: line.unit_of_measure,
              lineRemaining: remaining,
              quantity: Math.min(r.quantity || 1, remaining) || 1,
            }
          : r,
      ),
    );
  }

  function updateQty(rowKey: string, qty: number) {
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, quantity: qty } : r)));
  }

  function updatePrice(rowKey: string, value: string) {
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, unitPriceDollars: value } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, newRow()]);
  }

  function removeRow(rowKey: string) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== rowKey) : prev));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const modelRows: POBuildRowModel[] = rows
      .filter((r) => r.sourceRfqLineItemId)
      .map((r) => ({
        sourceRfqId: r.sourceRfqId,
        sourceRfqLineItemId: r.sourceRfqLineItemId,
        quantity: r.quantity,
        unitPrice: dollarsToCents(r.unitPriceDollars),
      }));
    const remainingById = new Map<string, number>();
    rows.forEach((r) => {
      if (r.sourceRfqLineItemId) remainingById.set(r.sourceRfqLineItemId, r.lineRemaining);
    });
    const validationError = validatePOBuildRows(modelRows, remainingById, vendorId);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const lines = modelRows.map((r) => ({
        source_rfq_line_item_id: r.sourceRfqLineItemId,
        quantity: r.quantity,
        unit_price: r.unitPrice,
      }));
      if (isEdit && editId) {
        const body: UpdatePurchaseOrderRequest = {
          vendor_id: vendorId,
          payment_terms: paymentTerms.trim() || undefined,
          delivery_terms: deliveryTerms.trim() || undefined,
          lines,
        };
        const res = await fetchWithAuth(`/api/purchase-orders/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const errJson = await res.json().catch(() => ({}) as { message?: string });
          setFormError(errJson?.message ?? "Failed to save changes.");
          return;
        }
        navigate({ to: "/purchase-orders/$id", params: { id: editId } });
        return;
      }
      const body: CreatePurchaseOrderRequest = {
        vendor_id: vendorId,
        payment_terms: paymentTerms.trim() || undefined,
        delivery_terms: deliveryTerms.trim() || undefined,
        lines,
      };
      const res = await fetchWithAuth("/api/purchase-orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status !== 201) {
        const errJson = await res.json().catch(() => ({}) as { message?: string });
        setFormError(errJson?.message ?? "Failed to create purchase order.");
        return;
      }
      const okJson = (await res.json()) as { data: PurchaseOrderDetailDto };
      navigate({ to: "/purchase-orders/$id", params: { id: okJson.data.id } });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Link
        to="/purchase-orders"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Purchase Orders
      </Link>

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-2">
        <h1 className="text-lg font-semibold tracking-tight">
          {isEdit ? "Edit draft purchase order" : "Build PO from RFQs"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? "Adjust the vendor, terms, and line items. Quantity changes re-sync the source RFQ's consumption."
            : "Draw line items from one or more RFQs into a single purchase order. Each line traces back to the RFQ line it came from and carries its negotiated price."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Vendor selector (required). */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Vendor</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              The purchase order is placed with a single Active vendor.
            </p>
          </div>
          <div className="px-5 py-4 max-w-md">
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger aria-label="Vendor">
                <SelectValue placeholder="Select a vendor…" />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id} data-testid={`vendor-option-${v.id}`}>
                    {v.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* Line builder — two cascading lookups + price per row. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Lines</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Pick a source RFQ, then a line of that RFQ. Quantity cannot exceed the remaining
                orderable amount.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              data-testid="add-line-button"
            >
              <Plus className="h-3.5 w-3.5" /> Add line
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/40">
                <tr>
                  <th className="px-4 py-2.5 text-left w-56">Source RFQ</th>
                  <th className="px-4 py-2.5 text-left">RFQ line</th>
                  <th className="px-3 py-2.5 text-left w-28">Remaining</th>
                  <th className="px-3 py-2.5 text-left w-24">Qty</th>
                  <th className="px-3 py-2.5 text-left w-32">Unit price</th>
                  <th className="px-3 py-2.5 w-12" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.key} className="border-t border-border align-top">
                    {/* Lookup 1 — source RFQ */}
                    <td className="px-4 py-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Line ${idx + 1} source RFQ`}
                            className="inline-flex w-full items-center justify-between gap-2 rounded border border-border bg-background px-2.5 py-2 text-left text-xs hover:bg-accent transition"
                          >
                            <span
                              className={
                                row.sourceRfqNumber ? "font-mono" : "text-muted-foreground"
                              }
                            >
                              {row.sourceRfqNumber || "Select an RFQ…"}
                            </span>
                            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-80 p-0 overflow-hidden">
                          <div className="max-h-60 overflow-y-auto">
                            {rfqs.length === 0 ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground">
                                No consumable RFQs available.
                              </p>
                            ) : (
                              <ul>
                                {rfqs.map((rfq) => (
                                  <li key={rfq.id}>
                                    <button
                                      type="button"
                                      data-testid={`rfq-option-${rfq.id}`}
                                      onClick={() => pickRfq(row.key, rfq)}
                                      className="grid w-full grid-cols-[8rem_minmax(0,1fr)] items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs transition hover:bg-accent first:border-t-0"
                                    >
                                      <span className="truncate font-mono font-medium">
                                        {rfq.rfq_number}
                                      </span>
                                      <span className="truncate text-muted-foreground">
                                        {rfq.status}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>

                    {/* Lookup 2 — RFQ line (only lines with remaining > 0) */}
                    <td className="px-4 py-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Line ${idx + 1} RFQ line`}
                            disabled={!row.sourceRfqId}
                            className="inline-flex w-full items-center justify-between gap-2 rounded border border-border bg-background px-2.5 py-2 text-left text-xs hover:bg-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className={row.lineDescription ? "" : "text-muted-foreground"}>
                              {row.lineDescription || "Select a line…"}
                            </span>
                            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-96 p-0 overflow-hidden">
                          <div className="max-h-60 overflow-y-auto">
                            {availableLines(row.sourceRfqId).length === 0 ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground">
                                No lines with remaining quantity on this RFQ.
                              </p>
                            ) : (
                              <ul>
                                {availableLines(row.sourceRfqId).map((line) => (
                                  <li key={line.id}>
                                    <button
                                      type="button"
                                      data-testid={`rfq-line-option-${line.id}`}
                                      onClick={() => pickLine(row.key, line)}
                                      className="grid w-full grid-cols-[minmax(0,1fr)_4rem] items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs transition hover:bg-accent first:border-t-0"
                                    >
                                      <span className="truncate">{line.description}</span>
                                      <span className="text-right tabular-nums text-muted-foreground">
                                        {rfqLineRemaining(line)} {line.unit_of_measure}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>

                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">
                      {row.sourceRfqLineItemId ? `${row.lineRemaining} ${row.lineUom}` : "—"}
                    </td>

                    <td className="px-3 py-2">
                      <Input
                        aria-label={`Line ${idx + 1} quantity`}
                        type="number"
                        min={1}
                        max={row.lineRemaining || undefined}
                        value={row.quantity}
                        disabled={!row.sourceRfqLineItemId}
                        onChange={(e) => updateQty(row.key, parseInt(e.target.value, 10) || 0)}
                      />
                    </td>

                    <td className="px-3 py-2">
                      <Input
                        aria-label={`Line ${idx + 1} unit price`}
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="0.00"
                        value={row.unitPriceDollars}
                        disabled={!row.sourceRfqLineItemId}
                        onChange={(e) => updatePrice(row.key, e.target.value)}
                      />
                    </td>

                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        aria-label={`Remove line ${idx + 1}`}
                        onClick={() => removeRow(row.key)}
                        disabled={rows.length === 1}
                        className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive transition disabled:opacity-30"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Optional terms. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Terms</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Optional payment + delivery terms for this order.
            </p>
          </div>
          <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <div className="space-y-1.5">
              <Label htmlFor="payment-terms">Payment terms</Label>
              <Input
                id="payment-terms"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="e.g. Net 30"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="delivery-terms">Delivery terms</Label>
              <Input
                id="delivery-terms"
                value={deliveryTerms}
                onChange={(e) => setDeliveryTerms(e.target.value)}
                placeholder="e.g. FOB Destination"
              />
            </div>
          </div>
        </section>

        {formError && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {formError}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="outline" type="button">
            <Link to="/purchase-orders">Cancel</Link>
          </Button>
          <Button type="submit" disabled={submitting} data-testid="submit-build-button">
            <Send className="h-3.5 w-3.5" />
            {submitting
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
                ? "Save changes"
                : "Create PO"}
          </Button>
        </div>
      </form>
    </div>
  );
}
